import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./FeedbackPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const HOTEL_ID = localStorage.getItem("ai2_hotel") || "";

const CATEGORIES = [
  { key: "cleanliness",  label: "Cleanliness" },
  { key: "service",      label: "Staff & Service" },
  { key: "room_comfort", label: "Room Comfort" },
  { key: "food",         label: "Food & Beverages" },
];

function StarRow({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="fb-star-row">
      <span className="fb-star-label">{label}</span>
      <div className="fb-stars">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            className={`fb-star ${n <= (hovered || value) ? "filled" : ""}`}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(n)}
            type="button"
          >★</button>
        ))}
      </div>
    </div>
  );
}

export default function FeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<"form" | "submitting" | "done" | "error" | "already">("form");

  const [rating, setRating] = useState(0);
  const [cats, setCats] = useState({ cleanliness: 0, service: 0, room_comfort: 0, food: 0 });
  const [comment, setComment] = useState("");
  const [wouldReturn, setWouldReturn] = useState<boolean | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const hotelId = HOTEL_ID || new URLSearchParams(window.location.search).get("h") || "";

  const submit = async () => {
    if (!rating) { setErrorMsg("Please give an overall rating"); return; }
    setStep("submitting");
    try {
      await axios.post(`${API}/api/feedback/submit/${token}`, {
        rating,
        ...cats,
        comment,
        would_return: wouldReturn,
        hotelId,
      });
      setStep("done");
    } catch (e: any) {
      if (e?.response?.status === 409) { setStep("already"); return; }
      setErrorMsg(e?.response?.data?.error || "Failed to submit. Please try again.");
      setStep("form");
    }
  };

  if (step === "done") return (
    <div className="fb-page">
      <div className="fb-card">
        <div className="fb-done-icon">✓</div>
        <h2 className="fb-done-title">Thank you!</h2>
        <p className="fb-done-sub">Your feedback has been submitted. We appreciate you taking the time.</p>
      </div>
    </div>
  );

  if (step === "already") return (
    <div className="fb-page">
      <div className="fb-card">
        <div className="fb-done-icon">✓</div>
        <h2 className="fb-done-title">Already submitted</h2>
        <p className="fb-done-sub">Feedback for this stay was already recorded. Thank you!</p>
      </div>
    </div>
  );

  return (
    <div className="fb-page">
      <div className="fb-card">
        <div className="fb-logo">★</div>
        <h1 className="fb-title">How was your stay?</h1>
        <p className="fb-sub">Your feedback helps us improve. It only takes a minute.</p>

        {/* Overall rating */}
        <div className="fb-overall-wrap">
          <div className="fb-overall-label">Overall Rating</div>
          <div className="fb-overall-stars">
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                className={`fb-overall-star ${n <= rating ? "filled" : ""}`}
                onClick={() => setRating(n)}
                type="button"
              >★</button>
            ))}
          </div>
          {rating > 0 && (
            <div className="fb-overall-label-text">
              {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
            </div>
          )}
        </div>

        {/* Category ratings */}
        <div className="fb-categories">
          {CATEGORIES.map(c => (
            <StarRow
              key={c.key}
              label={c.label}
              value={(cats as any)[c.key]}
              onChange={n => setCats(prev => ({ ...prev, [c.key]: n }))}
            />
          ))}
        </div>

        {/* Would return */}
        <div className="fb-return-wrap">
          <div className="fb-return-label">Would you stay with us again?</div>
          <div className="fb-return-btns">
            <button
              className={`fb-return-btn ${wouldReturn === true ? "active-yes" : ""}`}
              onClick={() => setWouldReturn(true)}
              type="button"
            >Yes</button>
            <button
              className={`fb-return-btn ${wouldReturn === false ? "active-no" : ""}`}
              onClick={() => setWouldReturn(false)}
              type="button"
            >No</button>
          </div>
        </div>

        {/* Comment */}
        <textarea
          className="fb-textarea"
          placeholder="Any additional comments or suggestions…"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={4}
        />

        {errorMsg && <div className="fb-error">{errorMsg}</div>}

        <button
          className="fb-submit-btn"
          onClick={submit}
          disabled={step === "submitting"}
        >
          {step === "submitting" ? "Submitting…" : "Submit Feedback"}
        </button>
      </div>
    </div>
  );
}
